/**
 * Pestaña de Financiación.
 *
 * Es la que cierra el hueco más grande que quedaba: nueve operaciones de escritura
 * —préstamos, seguros y gastos recurrentes— llevaban escritas, probadas y documentadas desde
 * la fase 2A sin una sola pantalla que las llamase. El desglose de gastos del resumen las
 * SUMABA, así que el usuario veía el resultado de unos datos que no podía tocar.
 *
 * Cada bloque enseña el número que importa, no todos los campos: de un préstamo, lo que se
 * mira es cuánto queda y cuánto se paga al mes; de una póliza, cuándo caduca. El resto vive
 * en el panel de edición.
 */
import { useState, type ReactNode } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import type {
  InsurancePolicyDto,
  LoanDto,
  PropertyFinancing as PropertyFinancingDto,
  RecurringExpenseDto,
  UnitDto,
} from '@naar/shared';
import { ApiError, api, queryKeys } from '../../api/client';
import { Badge } from '../../components/Badge';
import { Button } from '../../components/Button';
import { Card, CardHeader } from '../../components/Card';
import { Icon } from '../../components/Icon';
import { EmptyState } from '../../components/EmptyState';
import { ProgressBar } from '../../components/ProgressBar';
import { ConfirmDialog } from '../../components/overlay/Drawer';
import { toast } from '../../components/feedback/Toaster';
import { formatEur, formatPercent } from '../../lib/format';
import {
  FinancingFormDrawer,
  type FinancingKind,
  type FinancingRecord,
} from './FinancingFormDrawer';
import styles from './PropertyFinancing.module.css';

const FREQUENCY_LABELS: Record<string, string> = {
  mensual: 'al mes',
  trimestral: 'al trimestre',
  anual: 'al año',
};

const RATE_TYPE_LABELS: Record<string, string> = {
  fija: 'Fija',
  variable: 'Variable',
  mixta: 'Mixta',
};

/** Fecha corta en español a partir de un `YYYY-MM-DD`, sin arrastrar zonas horarias. */
function shortDate(iso: string): string {
  const [year, month, day] = iso.split('-');
  return `${Number(day)}/${Number(month)}/${year}`;
}

interface PropertyFinancingProps {
  propertyId: string;
  financing: PropertyFinancingDto;
  units: UnitDto[];
  canManage: boolean;
}

interface Editing {
  kind: FinancingKind;
  record?: FinancingRecord;
}

interface Deleting {
  kind: FinancingKind;
  record: FinancingRecord;
  name: string;
}

export function PropertyFinancing({
  propertyId,
  financing,
  units,
  canManage,
}: PropertyFinancingProps) {
  const queryClient = useQueryClient();
  const [editing, setEditing] = useState<Editing | null>(null);
  const [deleting, setDeleting] = useState<Deleting | null>(null);

  /**
   * Devuelve la promesa a propósito: el diálogo de borrado la espera antes de cerrarse, para
   * que la fila no siga un instante en pantalla después de haberla borrado.
   */
  function refresh(): Promise<unknown> {
    // Tocar la financiación mueve el patrimonio, la deuda y el cash flow de todos los inmuebles.
    void queryClient.invalidateQueries({ queryKey: ['properties'] });
    void queryClient.invalidateQueries({ queryKey: ['dashboard'] });
    return queryClient.invalidateQueries({ queryKey: queryKeys.property(propertyId) });
  }

  const remove = useMutation({
    mutationFn: ({ kind, record }: Deleting) => {
      if (kind === 'loan') return api.deleteLoan(record.id);
      if (kind === 'insurance') return api.deleteInsurance(record.id);
      return api.deleteExpense(record.id);
    },
    onSuccess: async () => {
      await refresh();
      toast.success('Eliminado.');
      setDeleting(null);
    },
    onError: (error: unknown) => {
      toast.error(error instanceof ApiError ? error.message : 'No se ha podido eliminar.');
    },
  });

  const totalDebt = financing.loans.reduce((sum, loan) => sum + Number(loan.outstandingBalance), 0);
  const monthlyLoans = financing.loans.reduce(
    (sum, loan) => sum + Number(loan.monthlyPayment),
    0,
  );
  const monthlyExpenses = financing.recurringExpenses
    .filter((expense) => expense.active)
    .reduce((sum, expense) => sum + Number(expense.monthlyAmount), 0);
  const monthlyInsurance = financing.insurance.reduce(
    (sum, policy) => sum + Number(policy.premium) / 12,
    0,
  );

  function addButton(kind: FinancingKind, label: string) {
    if (!canManage) return null;
    return (
      <Button variant="secondary" size="sm" onClick={() => setEditing({ kind })}>
        <Icon name="plus" size={15} />
        {label}
      </Button>
    );
  }

  function rowActions(kind: FinancingKind, record: FinancingRecord, name: string): ReactNode {
    if (!canManage) return null;
    return (
      <div className={styles.actions}>
        <button
          type="button"
          className={styles.iconButton}
          onClick={() => setEditing({ kind, record })}
          aria-label={`Editar ${name}`}
        >
          <Icon name="edit" size={16} />
        </button>
        <button
          type="button"
          className={`${styles.iconButton} ${styles.danger}`}
          onClick={() => setDeleting({ kind, record, name })}
          aria-label={`Eliminar ${name}`}
        >
          <Icon name="trash" size={16} />
        </button>
      </div>
    );
  }

  return (
    <div className={styles.wrapper}>
      {/* --- Lo que cuesta este inmueble al mes ---------------------------- */}
      <div className={styles.summary}>
        <SummaryTile label="Deuda pendiente" value={formatEur(String(totalDebt))} />
        <SummaryTile label="Cuotas de préstamo" value={`${formatEur(String(monthlyLoans))}/mes`} />
        <SummaryTile
          label="Seguros"
          value={`${formatEur(monthlyInsurance.toFixed(2))}/mes`}
          hint="La prima repartida entre doce meses."
        />
        <SummaryTile
          label="Gastos recurrentes"
          value={`${formatEur(monthlyExpenses.toFixed(2))}/mes`}
          hint="Solo los activos, ya normalizados."
        />
      </div>

      {/* --- Préstamos ------------------------------------------------------ */}
      <Card variant="sm">
        <CardHeader
          title="Préstamos"
          subtitle="La cuota entra directa en el desglose de gastos."
          aside={addButton('loan', 'Añadir préstamo')}
        />
        {financing.loans.length === 0 ? (
          <EmptyState
            inline
            icon="euro"
            title="Sin financiación registrada"
            body="Si el inmueble se compró al contado, no hay nada que añadir aquí."
          />
        ) : (
          <ul className={styles.list}>
            {financing.loans.map((loan) => (
              <LoanRow key={loan.id} loan={loan} actions={rowActions('loan', loan, loan.lender)} />
            ))}
          </ul>
        )}
      </Card>

      {/* --- Seguros -------------------------------------------------------- */}
      <Card variant="sm">
        <CardHeader
          title="Seguros"
          subtitle="Una póliza caducada deja el inmueble sin cobertura y genera alerta."
          aside={addButton('insurance', 'Añadir póliza')}
        />
        {financing.insurance.length === 0 ? (
          <EmptyState
            inline
            icon="file"
            title="Sin pólizas"
            body="Añade la del seguro de hogar para que la aplicación avise antes de que caduque."
          />
        ) : (
          <ul className={styles.list}>
            {financing.insurance.map((policy) => (
              <InsuranceRow
                key={policy.id}
                policy={policy}
                actions={rowActions('insurance', policy, policy.insurer)}
              />
            ))}
          </ul>
        )}
      </Card>

      {/* --- Gastos recurrentes --------------------------------------------- */}
      <Card variant="sm">
        <CardHeader
          title="Gastos recurrentes"
          subtitle="IBI, comunidad, suministros… Se guardan como se giran y se normalizan a mensual."
          aside={addButton('expense', 'Añadir gasto')}
        />
        {financing.recurringExpenses.length === 0 ? (
          <EmptyState
            inline
            icon="transfer"
            title="Sin gastos recurrentes"
            body="Sin ellos, el cash flow del inmueble sale más alto de lo que es en realidad."
          />
        ) : (
          <ul className={styles.list}>
            {financing.recurringExpenses.map((expense) => (
              <ExpenseRow
                key={expense.id}
                expense={expense}
                actions={rowActions('expense', expense, expense.concept)}
              />
            ))}
          </ul>
        )}
      </Card>

      {editing && (
        <FinancingFormDrawer
          open
          kind={editing.kind}
          propertyId={propertyId}
          record={editing.record}
          units={units}
          onClose={() => setEditing(null)}
          onSaved={() => void refresh()}
        />
      )}

      {deleting && (
        <ConfirmDialog
          title="Eliminar"
          body={`«${deleting.name}» se elimina del inmueble. Los indicadores que dependen de él se recalculan al instante.`}
          confirmLabel={remove.isPending ? 'Eliminando…' : 'Eliminar'}
          tone="danger"
          onConfirm={() => remove.mutate(deleting)}
          onCancel={() => setDeleting(null)}
        />
      )}
    </div>
  );
}

function SummaryTile({ label, value, hint }: { label: string; value: string; hint?: string }) {
  return (
    <div className={styles.tile}>
      <div className={styles.tileLabel}>{label}</div>
      <div className={styles.tileValue}>{value}</div>
      {hint && <div className={styles.tileHint}>{hint}</div>}
    </div>
  );
}

function LoanRow({ loan, actions }: { loan: LoanDto; actions: ReactNode }) {
  return (
    <li className={styles.row}>
      <div className={styles.rowMain}>
        <div className={styles.rowTitle}>
          {loan.lender}
          <Badge tone="neutral">{RATE_TYPE_LABELS[loan.rateType] ?? loan.rateType}</Badge>
        </div>
        <div className={styles.rowMeta}>
          {formatPercent(loan.interestRate)} · desde {shortDate(loan.startDate)}
          {loan.endDate ? ` hasta ${shortDate(loan.endDate)}` : ''}
        </div>
        {/* La barra dice de un vistazo cuánto se ha pagado ya, que es la pregunta real. */}
        <div className={styles.progress}>
          <ProgressBar percent={loan.amortizedPercent} />
          <span className={styles.progressLabel}>
            {formatPercent(loan.amortizedPercent)} amortizado
          </span>
        </div>
      </div>
      <div className={styles.rowFigures}>
        <div className={styles.figureMain}>{formatEur(loan.outstandingBalance)}</div>
        <div className={styles.figureMeta}>{formatEur(loan.monthlyPayment)}/mes</div>
      </div>
      {actions}
    </li>
  );
}

function InsuranceRow({
  policy,
  actions,
}: {
  policy: InsurancePolicyDto;
  actions: ReactNode;
}) {
  const expired = policy.daysToExpiry <= 0;
  const soon = !expired && policy.daysToExpiry <= 45;

  return (
    <li className={styles.row}>
      <div className={styles.rowMain}>
        <div className={styles.rowTitle}>
          {policy.insurer}
          {expired && <Badge tone="danger">Caducada</Badge>}
          {soon && <Badge tone="warning">Caduca pronto</Badge>}
        </div>
        <div className={styles.rowMeta}>
          {policy.coverageType ?? 'Cobertura no especificada'}
          {policy.policyNumber ? ` · nº ${policy.policyNumber}` : ''}
        </div>
        <div className={styles.rowMeta}>
          Vigente hasta {shortDate(policy.endDate)}
          {expired
            ? ` · caducó hace ${Math.abs(policy.daysToExpiry)} días`
            : ` · quedan ${policy.daysToExpiry} días`}
        </div>
      </div>
      <div className={styles.rowFigures}>
        <div className={styles.figureMain}>{formatEur(policy.premium)}</div>
        <div className={styles.figureMeta}>prima del periodo</div>
      </div>
      {actions}
    </li>
  );
}

function ExpenseRow({
  expense,
  actions,
}: {
  expense: RecurringExpenseDto;
  actions: ReactNode;
}) {
  return (
    <li className={expense.active ? styles.row : `${styles.row} ${styles.inactive}`}>
      <div className={styles.rowMain}>
        <div className={styles.rowTitle}>
          {expense.concept}
          {!expense.active && <Badge tone="neutral">Inactivo</Badge>}
          {expense.unit && <Badge tone="neutral">{expense.unit.code}</Badge>}
        </div>
        <div className={styles.rowMeta}>
          {formatEur(expense.amount)} {FREQUENCY_LABELS[expense.frequency] ?? expense.frequency}
          {expense.dayOfMonth ? ` · día ${expense.dayOfMonth}` : ''}
        </div>
      </div>
      <div className={styles.rowFigures}>
        <div className={styles.figureMain}>{formatEur(expense.monthlyAmount)}</div>
        <div className={styles.figureMeta}>normalizado al mes</div>
      </div>
      {actions}
    </li>
  );
}
